import { useState, useEffect } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const NewsletterPopup = ({ isOpen, onClose }) => {
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [quillInstance, setQuillInstance] = useState(null);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'check',
    'indent', 'align', 'link', 'image', 'video'
  ];

  useEffect(() => {
    if (!isOpen) {
      setEmailSubject('');
      setEmailContent('');
    }
  }, [isOpen]);

  const handleSendNewsletter = async () => {
    if (!emailSubject.trim()) {
      showToast('Please fill in the email subject.');
      return;
    }
    
    if (!emailContent.trim() || emailContent === '<p><br></p>') {
      showToast('Please write some email content.');
      return;
    }
    setIsSending(true);
    onClose();
    setTimeout(() => {
      console.log('Sending newsletter:', { 
        subject: emailSubject, 
        content: emailContent 
      });
      
      setEmailSubject('');
      setEmailContent('');
      setIsSending(false);
      
      showToast('Newsletter sent successfully!');
    }, 1500);
  };

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const slideInAnimation = {
    animation: 'slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards'
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
        style={{ animation: 'fadeIn 0.2s ease-out' }}
      />

      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div 
          className="bg-[#F8F9FE] rounded-xl shadow-2xl max-w-4xl w-full my-8"
          onClick={(e) => e.stopPropagation()} 
          style={{ animation: 'popupSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-white/50 rounded-t-xl">
            <h2 className="text-lg font-bold tracking-wide">Send Newsletter</h2>
            <button
              onClick={onClose}
              className="cursor-pointer text-zinc-400 hover:text-zinc-600 transition-colors duration-200 rounded-lg p-1"
              aria-label="Close popup"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 px-6 py-6">
            <div className="space-y-6">
              <div>
                <label htmlFor="emailSubject" className="block text-sm font-medium text-zinc-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  id="emailSubject"
                  name="emailSubject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="Enter newsletter subject"
                  autoFocus
                  style={{ height: '48px' }}
                />
              </div>

              <div>
                <label htmlFor="emailContent" className="block text-sm font-medium text-zinc-700 mb-2">
                  Email Content
                </label>
                
                <div className="mb-6 bg-white rounded-lg overflow-hidden" style={{ height: 'auto' }}> 
                  <ReactQuill
                    theme="snow"
                    value={emailContent}
                    onChange={setEmailContent}
                    modules={modules}
                    formats={formats}
                    placeholder="Write your newsletter content here..."
                    className="h-64"
                    style={{ height: 'auto' }}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="cursor-pointer px-6  bg-black text-white font-medium rounded-lg"
                  style={{ height: '48px' }}
                  disabled={isSending}
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleSendNewsletter}
                  disabled={isSending}
                  className="cursor-pointer px-6  bg-gradient-to-r from-amber-500 to-orange-500  text-white font-medium rounded-lg  disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ height: '48px' }}
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Newsletter
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toastMessage && (
        <div 
          className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-zinc-800 text-white px-4 py-2 rounded-lg shadow-lg z-[60]"
          style={slideInAnimation}
        >
          {toastMessage}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-50%) translateY(-100%); }
          to { transform: translateX(-50%) translateY(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes popupSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        /* Quill Editor Styles */
        .ql-container {
          font-size: 14px;
          border-radius: 0 0 8px 8px;
          min-height: 300px;
          height: auto !important;
        }
        
        .ql-toolbar {
          border-radius: 8px 8px 0 0;
          background-color: #fafafa;
          border-bottom: 1px solid #e5e7eb;
          border-top: none;
          border-left: none;
          border-right: none;
        }
        
        .ql-editor {
          min-height: 250px;
          height: auto !important;
          font-family: inherit;
        }
        
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        
        /* Make sure toolbar buttons are clickable */
        .ql-toolbar button {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        .ql-toolbar .ql-picker {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        .ql-toolbar .ql-picker-label {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        
        /* Ensure proper z-index for dropdowns */
        .ql-picker-options {
          z-index: 1000 !important;
        }
        
        /* Fix for button styles */
        .ql-toolbar button:hover {
          background-color: #e5e7eb !important;
        }
        
        .ql-toolbar button.ql-active {
          background-color: #e5e7eb !important;
          color: #f59e0b !important;
        }
        
        .ql-snow .ql-picker.ql-expanded .ql-picker-options {
          border-color: #e5e7eb;
        }
        
        /* Consistent height for all form elements */
        input,
        .ql-container,
        .ql-editor,
        button {
          transition: all 0.2s ease;
        }
        
        /* Make the Quill editor container consistent */
        .ql-container.ql-snow {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }
        
        /* Ensure proper spacing */
        .ql-editor {
          padding: 12px;
        }
      `}</style>
    </>
  );
};

export default NewsletterPopup;